import api from '../utils/api';

/**
 * @typedef {Object} Quiz
 * @property {string} _id
 * @property {string} title
 * @property {string} description
 * @property {string} course
 * @property {string} module
 * @property {string} lesson
 * @property {string[]} questions
 * @property {number} totalQuestions
 * @property {number} duration - in minutes
 * @property {number} totalMarks
 * @property {number} passingScore
 * @property {number} maxAttempts
 * @property {boolean} shuffleQuestions
 * @property {boolean} shuffleAnswers
 * @property {boolean} showResultsImmediately
 * @property {boolean} showCorrectAnswers
 * @property {boolean} showExplanations
 * @property {boolean} isPublished
 * @property {string} publishedAt
 * @property {boolean} isMandatory
 * @property {string} instructions
 * @property {string} availableFrom
 * @property {string} availableUntil
 * @property {Object} pmiDetails
 * @property {string} createdAt
 * @property {string} updatedAt
 */

/**
 * @typedef {Object} Question
 * @property {string} _id
 * @property {string} text
 * @property {string} type - 'multiple_choice', etc
 * @property {string[]} options
 * @property {number} points
 */

/**
 * @typedef {Object} CreateQuizData
 * @property {string} title
 * @property {string} description
 * @property {string} courseId
 * @property {string} [moduleId]
 * @property {number} passingScore
 * @property {number} duration
 */

export const quizService = {
  // ==================
  // PUBLIC ROUTES
  // ==================

  /**
   * Get quizzes by course
   * @param {string} courseId 
   * @returns {Promise<{status: string, quizzes: Quiz[]}>}
   */
  getQuizzesByCourse: async (courseId) => {
    const response = await api.get(`/api/quizzes/course/${courseId}`);
    return response;
  },

  /**
   * Get PMI quizzes
   * @returns {Promise<{status: string, count: number, quizzes: Quiz[]}>}
   */
  getPMIQuizzes: async () => {
    const response = await api.get('/api/quizzes/pmi');
    return response;
  },

  /**
   * Get all quizzes (Administrator)
   * @param {Object} params - Query parameters (page, limit, search, etc.)
   * @returns {Promise<{status: string, results: number, pagination: Object, data: {quizzes: Quiz[]}}>}
   */
  getAllQuizzes: async (params = {}) => {
    const response = await api.get('/api/quizzes', { params });
    return response;
  },

  /**
   * Get quiz by ID
   * @param {string} id 
   * @returns {Promise<{status: string, data: {quiz: Quiz}}>}
   */
  getQuizById: async (id) => {
    const response = await api.get(`/api/quizzes/${id}`);
    return response;
  },

  // ==================
  // STUDENT ROUTES
  // ==================

  /**
   * Start quiz attempt
   * @param {string} quizId 
   * @returns {Promise<{status: string, attemptId: string, startTime: string}>}
   */
  startQuizAttempt: async (quizId) => {
    const response = await api.post(`/api/quizzes/${quizId}/start`);
    return response;
  },

  /**
   * Submit quiz attempt
   * @param {string} attemptId 
   * @param {Object} answers 
   * @returns {Promise<{status: string, score: number, passed: boolean}>}
   */
  submitQuizAttempt: async (attemptId, answers) => {
    const response = await api.post(`/api/quizzes/attempts/${attemptId}/submit`, { answers });
    return response;
  },

  /**
   * Get attempt result
   * @param {string} attemptId 
   * @returns {Promise<{status: string, attempt: Object}>}
   */
  getAttemptResult: async (attemptId) => {
    const response = await api.get(`/api/quizzes/attempts/${attemptId}/result`);
    return response;
  },

  /**
   * Get user attempts
   * @returns {Promise<{status: string, count: number, attempts: Object[]}>}
   */
  getUserAttempts: async () => {
    const response = await api.get('/api/quizzes/user/attempts');
    return response;
  },

  // ==================
  // ADMIN ROUTES
  // ==================


  /**
   * Create quiz
   * @param {CreateQuizData} quizData 
   * @returns {Promise<{status: string, quiz: Quiz}>}
   */
  createQuiz: async (quizData) => {
    const response = await api.post('/api/quizzes', quizData);
    return response;
  },

  /**
   * Create PMI quiz
   * @param {CreateQuizData} quizData 
   * @returns {Promise<{status: string, quiz: Quiz}>}
   */
  createPMIQuiz: async (quizData) => {
    const response = await api.post('/api/quizzes/pmi/create', quizData);
    return response;
  },

  /**
   * Update quiz
   * @param {string} id 
   * @param {Object} updates 
   * @returns {Promise<{status: string, quiz: Quiz}>}
   */
  updateQuiz: async (id, updates) => {
    const response = await api.put(`/api/quizzes/${id}`, updates);
    return response;
  },

  /**
   * Delete quiz
   * @param {string} id 
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteQuiz: async (id) => {
    const response = await api.delete(`/api/quizzes/${id}`);
    return response;
  },

  /**
   * Toggle publish quiz
   * @param {string} id 
   * @returns {Promise<{status: string, message: string, isPublished: boolean}>}
   */
  togglePublishQuiz: async (id) => {
    const response = await api.patch(`/api/quizzes/${id}/toggle-publish`);
    return response;
  },

  /**
   * Get quiz statistics
   * @param {string} quizId 
   * @returns {Promise<{status: string, statistics: Object}>}
   */
  getQuizStatistics: async (quizId) => {
    const response = await api.get(`/api/quizzes/${quizId}/statistics`);
    return response;
  },

  // ==================
  // QUESTION MANAGEMENT
  // ==================

  /**
   * Get quiz questions
   * @param {string} quizId 
   * @param {boolean} [includeAnswers]
   * @returns {Promise<{status: string, results: number, data: {questions: Question[]}}>}
   */
  getQuizQuestions: async (quizId, includeAnswers = false) => {
    const response = await api.get(`/api/quizzes/${quizId}/questions`, { params: { includeAnswers: includeAnswers.toString() } });
    return response;
  },

  /**
   * Add question to quiz
   * @param {string} quizId 
   * @param {Object} questionData 
   * @returns {Promise<{status: string, data: {question: Question}}>}
   */
  addQuestionToQuiz: async (quizId, questionData) => {
    const response = await api.post(`/api/quizzes/${quizId}/questions`, questionData);
    return response;
  },

  /**
   * Add bulk questions
   * @param {string} quizId 
   * @param {Object[]} questions 
   * @returns {Promise<{status: string, count: number}>}
   */
  addBulkQuestions: async (quizId, questions) => {
    const response = await api.post(`/api/quizzes/${quizId}/questions/bulk`, { questions });
    return response;
  },

  /**
   * Update question
   * @param {string} questionId 
   * @param {Object} updates 
   * @returns {Promise<{status: string, data: {question: Question}}>}
   */
  updateQuestion: async (questionId, updates) => {
    const response = await api.put(`/api/quizzes/questions/${questionId}`, updates);
    return response;
  },

  /**
   * Delete question
   * @param {string} questionId 
   * @returns {Promise<{status: string, message: string}>}
   */
  deleteQuestion: async (questionId) => {
    const response = await api.delete(`/api/quizzes/questions/${questionId}`);
    return response;
  },
};
